// src/lib/auth.js
import { writable, get } from 'svelte/store';
import {
	getAuth,
	onAuthStateChanged,
	signInWithRedirect,
	signOut as _signOut,
	GoogleAuthProvider,
	FacebookAuthProvider,
	isSignInWithEmailLink,
	signInWithEmailLink,
	signInAnonymously,
	setPersistence,
	browserLocalPersistence,
	signInWithEmailAndPassword,
	sendPasswordResetEmail,
	createUserWithEmailAndPassword,
	updateProfile,
	sendSignInLinkToEmail,
	getRedirectResult,
	confirmPasswordReset,
	onIdTokenChanged,
	updateEmail,
	linkWithCredential,
	EmailAuthProvider
} from 'firebase/auth';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { firestoreAuth, firestoreDb, getClientApp } from './firebase';
import { browser } from '$app/environment';
import { userStore } from '$stores/user';
import { createUserDoc } from './firestore/createUserDoc';
import { createUserFromLink } from './firestore/createUserFromLink';
import { goto } from '$app/navigation';
import posthog from 'posthog-js';
import { checkUserObject } from './firestore/checkUserObject';
import { showErrorToast, showToast } from './helpers';
import { logError } from './helpers/errors';
import { trackReferral } from './referrals';
import { fetchOrdersCount } from '$stores/ordersCount';
import { trackKlaviyo } from '$lib/klaviyo/events';
import { loadingMessage } from '$stores/loadingMessage';

const actionCodeSettings = {
	url: 'https://www.hotdraw.co.uk',
	handleCodeInApp: true
};

/* ------------------------------------------------------------------ */
/*  helpers                                                           */
/* ------------------------------------------------------------------ */
const normaliseEmail = (email = '') => email.trim().toLowerCase();

function splitDisplayName(displayName = '') {
	const parts = displayName.trim().split(' ');
	const first = parts.shift() || '';
	const last = parts.join(' ') || '';
	return { first, last };
}

/* ------------------------------------------------------------------ */
/*  auth store                                                        */
/* ------------------------------------------------------------------ */
const createAuth = () => {
	const { subscribe, set } = writable({ user: null, known: false });

	if (!browser) return { subscribe }; // SSR guard

	const auth = firestoreAuth;

	/* ------------------------------------------------------------------ */
	/*  listeners                                                         */
	/* ------------------------------------------------------------------ */
	async function listen() {
		/* --- 1. email-link sign-in ----------------------------------- */
		if (isSignInWithEmailLink(auth, window.location.href)) {
			loadingMessage.set('Logging you in…');
			let email = window.localStorage.getItem('emailForSignIn') || '';
			if (!email) email = window.prompt('Please enter the e-mail you used to sign up') || '';
			email = normaliseEmail(email);

			try {
				const { user } = await signInWithEmailLink(auth, email);
				loadingMessage.set('Getting your account…');
				window.localStorage.removeItem('emailForSignIn'); // ← clear it
				window.history.replaceState({}, '', window.location.pathname);
				set({ user, known: true });

				const exists = await checkUserObject(email);
				if (!exists) await createUserFromLink(user.uid, { email });

				posthog.capture('magic_link_login_success', { email });
				showToast('Success', 'Login successful! Redirecting…');
				setTimeout(() => goto('/prizes'), 1500);
				loadingMessage.set('');
			} catch (err) {
				logError(err, 'signInWithEmailLink');
				showErrorToast('Login error', err.message);
				posthog.capture('magic_link_login_error', { email, message: err.message, code: err.code });
				window.history.replaceState({}, '', window.location.pathname);
				loadingMessage.set('');
			}
		}

		/* --- 2. password-reset flow ---------------------------------- */
		const params = new URLSearchParams(window.location.search);
		const mode = params.get('mode');
		const oobCode = params.get('oobCode');

		if (mode === 'resetPassword' && oobCode) {
			const newPass = window.prompt('Enter your new password');
			if (!newPass) return;
			try {
				await confirmPasswordReset(auth, oobCode, newPass);
				showToast('Password reset successful', 'Please log in with your new password');
				goto('/login');
			} catch (err) {
				logError(err, 'confirmPasswordReset');
				showErrorToast('Password reset failed', err.message);
			}
		}

		/* --- 3. auth state ------------------------------------------- */
		onAuthStateChanged(auth, async (user) => {
			if (!user) {
				set({ user: null, known: true });
				return;
			}

			set({ user, known: true });

			const email = user.email ? normaliseEmail(user.email) : '';
			const anon = user.isAnonymous;
			const display = splitDisplayName(user.displayName || '');
			const partial = { firstName: display.first, lastName: display.last };

			if (email) {
				await ensureUserDocument(email, partial);
			} else if (anon) {
				const guestEmail = normaliseEmail(get(userStore).email || '');
				if (guestEmail) await ensureUserDocument(guestEmail, partial);
			}

			/* analytics & onboarding */
			const { creationTime, lastSignInTime } = user.metadata;
			const fresh = new Date(lastSignInTime).getTime() - new Date(creationTime).getTime() < 1_000;

			posthog.capture(fresh ? 'new_user_logged_in' : 'returning_user_logged_in', { email });
			// fetchOrdersCount(email);
			if (typeof window !== 'undefined') {
				window.klaviyo?.identify({ email });
			}
			trackKlaviyo(fresh ? 'New User Logged In' : 'Returning User Logged In', {
				properties: { email }
			});
		});

		/* --- 4. Google / FB redirect --------------------------------- */
		try {
			const res = await getRedirectResult(auth);
			if (res?.user) {
				const googleUser = res.user;
				const email = normaliseEmail(googleUser.email || '');
				const { first, last } = splitDisplayName(googleUser.displayName || '');

				await ensureUserDocument(email, { firstName: first, lastName: last });
				goto('/prizes');
			}
		} catch (err) {
			logError(err, 'getRedirectResult');
			showErrorToast('Social login failed', err.message);
		}
	}

	/**  Make sure exactly one users doc exists for this e-mail.
	 *   Never overwrite non-empty fields with blank strings.        */
	async function ensureUserDocument(email, partial = {}) {
		if (!email) return;

		const mail = normaliseEmail(email);
		const db = await firestoreDb();

		// 1️⃣  Look for any existing doc
		const match = await getDocs(query(collection(db, 'users'), where('email', '==', mail)));
		const ref = match.empty
			? doc(db, 'users', mail) // canonical path
			: match.docs[0].ref; // first match (could be UID-named)

		/* 2️⃣  Build payload – set createdAt / roles **only** the first time */
		const base = match.empty ? { createdAt: Date.now(), roles: ['CUSTOMER'] } : {};

		const payload = Object.fromEntries(
			Object.entries({
				email: mail,
				...base,
				...partial // { firstName, lastName, … }
			}).filter(
				([_, v]) => !(typeof v === 'string' && v.trim() === '') && v !== undefined && v !== null
			)
		);

		/* 3️⃣  Merge */
		try {
			await setDoc(ref, payload, { merge: true });
		} catch (err) {
			logError(err, 'ensureUserDocument');
		}
	}

	/* ------------------------------------------------------------------ */
	/*  provider helpers                                                  */
	/* ------------------------------------------------------------------ */
	function providerFor(name) {
		switch (name) {
			case 'google':
				return new GoogleAuthProvider();
			case 'facebook':
				return new FacebookAuthProvider();
			default:
				throw new Error(`Unknown provider: ${name}`);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  public API                                                        */
	/* ------------------------------------------------------------------ */
	async function signInWith(name) {
		try {
			await signInWithRedirect(auth, providerFor(name));
		} catch (err) {
			logError(err, 'signInWithRedirect');
			showErrorToast('Login error', err.message);
		}
	}

	async function signOut() {
		try {
			await _signOut(auth);
			userStore.set({
				firstName: '',
				lastName: '',
				email: '',
				phone: '',
				acceptTerms: false,
				address: '',
				postCode: '',
				country: ''
			});
			posthog.reset(true);
		} catch (err) {
			logError(err, 'signOut');
			showErrorToast('Logout error', err.message);
		}
	}

	/* ----------  email link sign-in  ---------------------------------- */
	async function emailLink(email) {
		try {
			await sendSignInLinkToEmail(auth, normaliseEmail(email), actionCodeSettings);
			window.localStorage.setItem('emailForSignIn', normaliseEmail(email));
			return 200;
		} catch (err) {
			logError(err, 'sendSignInLinkToEmail');
			return 500;
		}
	}

	/* ----------  anonymous + link  ------------------------------------ */
	async function anonSignIn(email, password = '') {
		const mail = normaliseEmail(email);

		try {
			await setPersistence(auth, browserLocalPersistence);
			await signInAnonymously(auth);

			/* try to link the anon user to e-mail/password */
			const cred = EmailAuthProvider.credential(mail, password);
			try {
				await linkWithCredential(auth.currentUser, cred);
			} catch (err) {
				if (err.code === 'auth/email-already-in-use') {
					await signInWithEmailAndPassword(auth, mail, password);
				} else if (err.code === 'auth/invalid-credential') {
					showErrorToast('Wrong password', 'Double-check your password and try again.');
					return;
				} else {
					throw err;
				}
			}

			userStore.update((u) => ({ ...u, email: mail }));
			await ensureUserDocument(mail);
		} catch (err) {
			logError(err, 'anonSignIn');
			showErrorToast('Guest login failed', err.message);
		}
	}

	/* ----------  password sign-in  ------------------------------------ */
	async function passwordSignIn(email, password) {
		const mail = normaliseEmail(email);
		try {
			const { user } = await signInWithEmailAndPassword(auth, mail, password);
			set({ user, known: true });

			userStore.set({
				email: mail,
				firstName: '',
				lastName: '',
				phone: '',
				acceptTerms: false,
				address: '',
				postCode: '',
				country: ''
			});

			posthog.capture('password_login_success', { email: mail });
			showToast('Success', 'Login successful! Redirecting…');
			setTimeout(() => goto('/prizes'), 1500);
		} catch (err) {
			logError(err, 'passwordSignIn');
			showErrorToast('Login error', err.message);
			posthog.capture('password_login_error', {
				email: mail,
				message: err.message,
				code: err.code
			});
		}
	}

	/* ----------  email/password signup  ------------------------------- */
	async function createUserBasic(email, password = '', first = '', last = '', phone = '') {
		const mail = normaliseEmail(email);

		try {
			const { user } = await createUserWithEmailAndPassword(auth, mail, password);

			await ensureUserDocument(mail, {
				firstName: first,
				lastName: last,
				phone
			});

                        await updateProfile(user, { displayName: `${first} ${last}`.trim() });

                        posthog.reset(true);
                        posthog.group('authenticated', mail, { name: `${first} ${last}`, email: mail });
                        if (typeof window !== 'undefined' && window.rdt) {
                                window.rdt('track', 'SignUp');
                        }
                        goto('/prizes');
                } catch (err) {
                        logError(err, 'createUserBasic');
                        if (err.code === 'auth/email-already-in-use') {
                                showErrorToast('E-mail in use', 'Try logging in instead.');
			} else {
				showErrorToast('Signup error', err.message);
			}
		}
	}

	/* ----------  reset password  -------------------------------------- */
	async function resetPasswordEmail(email) {
		const mail = normaliseEmail(email);
		try {
			await sendPasswordResetEmail(auth, mail, actionCodeSettings);
			showToast('Email sent', 'Check your inbox for the reset link.');
			return '200';
		} catch (err) {
			logError(err, 'resetPasswordEmail');
			showErrorToast('Reset failed', err.message);
			return err;
		}
	}

	/* ----------  start all listeners  --------------------------------- */
	listen();

	return {
		subscribe,
		signInWith,
		signOut,
		emailLink,
		anonSignIn,
		passwordSignIn,
		resetPasswordEmail,
		createUserBasic,
		auth: getAuth(getClientApp())
	};
};

export const auth = createAuth();

/* ------------------------------------------------------------------ */
/*  ID-token → server-session sync                                    */
/* ------------------------------------------------------------------ */
export function setupAuthListener() {
	const authInstance = auth.auth;

	onIdTokenChanged(authInstance, async (user) => {
		if (!user) {
			try {
				await fetch('/api/logout', { method: 'POST', credentials: 'include' });
			} catch (err) {
				logError(err, 'session logout');
			}
			return;
		}

		try {
			const token = await user.getIdToken();
			await fetch('/api/login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token }),
				credentials: 'include'
			});
		} catch (err) {
			logError(err, 'session cookie');
		}
	});
}
